import type * as CC from 'vanilla-cookieconsent';
import type { CookieRows } from './cookie-table';
import { SERVICE_PRESETS, isServiceKey } from './services';
import { buildTranslation, textsFor } from './translations';
import {
  OPTIONAL_CATEGORIES,
  type CategoryName,
  type DVConsentConfig,
  type DeepPartial,
  type OptionalCategory,
  type ServiceKey,
} from './types';

export const COOKIE_NAME = 'kockica_consent';
export const DEFAULT_EXPIRES_DAYS = 182;

export interface ResolvedConfig {
  siteId: string;
  controllerName: string;
  policyUrl: string;
  languages: string[];
  autoDetectLanguage: 'document' | 'browser';
  services: ServiceKey[];
  /** Optional categories shown, in fixed order: functional, analytics, marketing. */
  categories: OptionalCategory[];
  /** Categories the site listed itself (kept when detected trackers are merged in). */
  explicitCategories: OptionalCategory[];
  autoBlock: boolean;
  googleMode: 'advanced' | 'basic';
  consentMode: boolean;
  logEndpoint: string | null;
  revision: number;
  expiresAfterDays: number;
  cookieDomain: string | undefined;
  layout: 'box' | 'bar';
  position: string;
  revisitButton: boolean;
  revisitPosition: 'left' | 'right';
  branding: boolean;
  respectGpc: boolean;
  translations: Record<string, DeepPartial<CC.Translation>>;
}

export class DVConsentConfigError extends Error {
  constructor(message: string) {
    super(`[@datavallis/consent] ${message}`);
    this.name = 'DVConsentConfigError';
  }
}

function required(value: unknown, name: string): string {
  if (typeof value !== 'string' || value.trim() === '') throw new DVConsentConfigError(`"${name}" is required.`);
  return value.trim();
}

/** Validate input and fill in defaults. Throws DVConsentConfigError on invalid config — fail loud, never silently. */
export function resolveConfig(config: DVConsentConfig): ResolvedConfig {
  if (!config || typeof config !== 'object') throw new DVConsentConfigError('Config object is required.');
  const siteId = required(config.siteId, 'siteId');
  const controllerName = required(config.controller?.name, 'controller.name');
  const policyUrl = required(config.policyUrl, 'policyUrl');

  const services = config.services ?? [];
  for (const s of services) {
    if (!isServiceKey(s)) throw new DVConsentConfigError(`Unknown service "${String(s)}".`);
  }
  for (const c of config.categories ?? []) {
    if (!OPTIONAL_CATEGORIES.includes(c)) throw new DVConsentConfigError(`Unknown category "${String(c)}".`);
  }
  const wanted = new Set<OptionalCategory>([
    ...(config.categories ?? []),
    ...services.map((s) => SERVICE_PRESETS[s].category),
  ]);
  const categories = OPTIONAL_CATEGORIES.filter((c) => wanted.has(c));

  const languages = (config.languages ?? ['sl', 'en']).map((l) => l.trim().toLowerCase()).filter(Boolean);
  if (languages.length === 0) throw new DVConsentConfigError('"languages" must contain at least one language.');

  const revision = config.revision ?? 0;
  if (!Number.isInteger(revision) || revision < 0) throw new DVConsentConfigError('"revision" must be a whole number ≥ 0.');

  const expiresAfterDays = config.expiresAfterDays ?? DEFAULT_EXPIRES_DAYS;
  if (!Number.isInteger(expiresAfterDays) || expiresAfterDays < 1 || expiresAfterDays > 395) {
    throw new DVConsentConfigError('"expiresAfterDays" must be a whole number between 1 and 395 (13 months).');
  }

  const logEndpoint = config.logEndpoint?.trim() || null;
  if (logEndpoint && !/^https:\/\//i.test(logEndpoint) && !/^http:\/\/localhost[:/]/i.test(logEndpoint)) {
    throw new DVConsentConfigError('"logEndpoint" must be an https:// URL.');
  }

  const layout = config.layout ?? 'box';
  return {
    siteId,
    controllerName,
    policyUrl,
    languages: [...new Set(languages)],
    autoDetectLanguage: config.autoDetectLanguage ?? 'document',
    services: [...new Set(services)],
    categories,
    explicitCategories: OPTIONAL_CATEGORIES.filter((c) => (config.categories ?? []).includes(c)),
    autoBlock: config.autoBlock ?? true,
    googleMode: config.googleMode === 'basic' ? 'basic' : 'advanced',
    consentMode: config.consentMode ?? true,
    logEndpoint,
    revision,
    expiresAfterDays,
    cookieDomain: config.cookieDomain,
    layout,
    position: config.position ?? (layout === 'bar' ? 'bottom' : 'bottom left'),
    revisitButton: config.revisitButton ?? true,
    revisitPosition: config.revisitPosition === 'right' ? 'right' : 'left',
    branding: config.branding ?? true,
    respectGpc: config.respectGpc ?? false,
    translations: config.translations ?? {},
  };
}

/**
 * Merge trackers detected on the page into the config: their services and categories are offered in the banner.
 * Google Tag Manager can carry any tag, so it brings in analytics and marketing.
 */
export function withDetected(resolved: ResolvedConfig, detected: readonly ServiceKey[]): ResolvedConfig {
  const services = [...new Set([...resolved.services, ...detected])];
  const wanted = new Set<OptionalCategory>([...resolved.explicitCategories, ...services.map((s) => SERVICE_PRESETS[s].category)]);
  if (services.includes('gtm')) {
    wanted.add('analytics');
    wanted.add('marketing');
  }
  return { ...resolved, services, categories: OPTIONAL_CATEGORIES.filter((c) => wanted.has(c)) };
}

/** Recursively merge plain objects; arrays and primitives in `override` replace the base. */
export function deepMerge<T>(base: T, override: unknown): T {
  if (!isPlainObject(base) || !isPlainObject(override)) return (override === undefined ? base : override) as T;
  const out: Record<string, unknown> = { ...base };
  for (const [key, value] of Object.entries(override)) {
    if (value === undefined) continue;
    out[key] = isPlainObject(out[key]) && isPlainObject(value) ? deepMerge(out[key], value) : value;
  }
  return out as T;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function offeredCategories(resolved: ResolvedConfig): CategoryName[] {
  return ['necessary', ...resolved.categories];
}

export interface Callbacks {
  onFirstConsent: CC.CookieConsentConfig['onFirstConsent'];
  onConsent: CC.CookieConsentConfig['onConsent'];
  onChange: CC.CookieConsentConfig['onChange'];
}

/** Build the vanilla-cookieconsent config. Pure: no DOM access, so it is unit-tested directly. */
export function buildCookieConsentConfig(
  resolved: ResolvedConfig,
  callbacks: Callbacks,
  cookieRows?: (language: string) => CookieRows,
): CC.CookieConsentConfig {
  const categories: Record<string, CC.Category> = {
    necessary: { enabled: true, readOnly: true },
  };
  for (const category of resolved.categories) {
    const presets = resolved.services.filter((s) => SERVICE_PRESETS[s].category === category);
    const services: Record<string, CC.Service> = {};
    for (const key of presets) {
      const preset = SERVICE_PRESETS[key];
      services[key] = {
        label: preset.label,
        cookies: preset.cookies,
        ...(preset.onAccept ? { onAccept: preset.onAccept } : {}),
        ...(preset.onReject ? { onReject: preset.onReject } : {}),
      };
    }
    const cookies = presets.flatMap((s) => SERVICE_PRESETS[s].cookies);
    categories[category] = {
      // Opt-in: nothing optional is on until the visitor turns it on (no pre-ticked boxes).
      enabled: false,
      readOnly: false,
      ...(presets.length ? { services } : {}),
      ...(cookies.length ? { autoClear: { cookies } } : {}),
    };
  }

  const translations: Record<string, CC.Translation> = {};
  for (const lang of resolved.languages) {
    const base = buildTranslation(textsFor(lang), {
      controllerName: resolved.controllerName,
      policyUrl: resolved.policyUrl,
      categories: resolved.categories,
      branding: resolved.branding,
      ...(cookieRows ? { cookieRows: cookieRows(lang) } : {}),
    });
    translations[lang] = deepMerge(base, resolved.translations[lang]);
  }

  const cookie: CC.CookieOptions = {
    name: COOKIE_NAME,
    expiresAfterDays: resolved.expiresAfterDays,
    sameSite: 'Lax',
  };
  if (resolved.cookieDomain) cookie.domain = resolved.cookieDomain;

  return {
    mode: 'opt-in',
    revision: resolved.revision,
    autoShow: true,
    manageScriptTags: true,
    autoClearCookies: true,
    hideFromBots: true,
    disablePageInteraction: false,
    respectGpc: resolved.respectGpc,
    cookie,
    guiOptions: {
      consentModal: {
        layout: resolved.layout,
        position: resolved.position as CC.ConsentModalPosition,
        // Accept and Reject look identical (EDPB Cookie Banner Taskforce: no deceptive design).
        equalWeightButtons: true,
        flipButtons: false,
      },
      preferencesModal: {
        layout: 'box',
        equalWeightButtons: true,
        flipButtons: false,
      },
    },
    categories,
    language: {
      default: resolved.languages[0] as string,
      autoDetect: resolved.autoDetectLanguage,
      translations,
    },
    onFirstConsent: callbacks.onFirstConsent,
    onConsent: callbacks.onConsent,
    onChange: callbacks.onChange,
  };
}
